import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faUsers,
  faBox,
  faExpand,
  faCompress,
} from "@fortawesome/pro-light-svg-icons";
import Button from "../../../../buttons/Button";

const DailyView = ({
  date,
  items = [],
  tables = [],
  defaultDuration = 2,
  onViewItem,
  isFullscreen = false,
  onToggleFullscreen,
  tKey = "dashboard.bookings.calendar.",
}) => {
  const { t } = useTranslation();
  const dateStr = date.toISOString().split("T")[0];

  const isCanceledItem = (item) =>
    item.displayStatus === "canceled" || item.displayStatus === "declined";

  // Filter items for this day, sort canceled after others
  const dayItems = items
    .filter((item) => {
      const itemDate = new Date(item.bookingDate).toISOString().split("T")[0];
      return itemDate === dateStr;
    })
    .sort((a, b) => {
      const aCanceled = isCanceledItem(a) ? 1 : 0;
      const bCanceled = isCanceledItem(b) ? 1 : 0;
      if (aCanceled !== bCanceled) return aCanceled - bCanceled;
      return new Date(a.bookingDate) - new Date(b.bookingDate);
    });

  const activeItemCount = dayItems.filter((item) => !isCanceledItem(item)).length;

  // Time slots from 11:00 to 23:30 (30-min increments)
  const timeSlots = Array.from({ length: 26 }, (_, i) => {
    const hour = Math.floor(i / 2) + 11;
    const minute = (i % 2) * 30;
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  });

  // Flatten rooms → tables
  const allTables = tables.flatMap((room) =>
    (room.tables || [])
      .filter((tbl) => tbl.isActive)
      .map((tbl) => ({
        ...tbl,
        roomName: room.name,
      })),
  );

  // Virtual "No table" column for unassigned items
  const NO_TABLE_ID = "__no_table__";

  // Find items for a table at a specific time slot
  const getItemsForTableAndTime = (tableId, timeSlot) => {
    const [slotHour, slotMinute] = timeSlot.split(":").map(Number);
    const slotMinutes = slotHour * 60 + slotMinute;
    const slotEnd = slotMinutes + 30;
    const durationMinutes = defaultDuration * 60;

    return dayItems.filter((item) => {
      // Check table assignment
      if (tableId === NO_TABLE_ID) {
        // Show items that have no table assigned
        const hasTable = item.tableBookings?.length > 0;
        if (hasTable) return false;
      } else {
        const isAssigned = item.tableBookings?.some(
          (tb) => tb.tableId === tableId,
        );
        if (!isAssigned) return false;
      }

      const itemDate = new Date(item.bookingDate);
      const itemStart = itemDate.getHours() * 60 + itemDate.getMinutes();
      const itemEnd = itemStart + durationMinutes;

      // Item overlaps this slot if: item starts before slot ends AND item ends after slot starts
      return itemStart < slotEnd && itemEnd > slotMinutes;
    });
  };

  // Check if an item starts within this 30-min slot (to only render the card once)
  const isItemStart = (item, timeSlot) => {
    const itemDate = new Date(item.bookingDate);
    const itemMinutes = itemDate.getHours() * 60 + itemDate.getMinutes();
    const [slotHour, slotMinute] = timeSlot.split(":").map(Number);
    const slotMinutes = slotHour * 60 + slotMinute;
    return itemMinutes >= slotMinutes && itemMinutes < slotMinutes + 30;
  };

  // Build the columns: "No table" always first, then actual tables
  const columns = [
    {
      id: NO_TABLE_ID,
      name: t(`${tKey}noTable`),
      info: null,
      isNoTable: true,
    },
  ];
  allTables.forEach((table) => {
    columns.push({
      id: table.tableId,
      name: table.name,
      info: `${table.roomName} \u2022 ${table.numberOfPlaces} ${t("dashboard.bookings.detail.guests")}`,
      isNoTable: false,
    });
  });

  return (
    <div className="bookings-daily">
      <div className="bookings-daily__header bookings-view__header">
        <div>
          <h4 className="bookings-daily__title">
            {date.toLocaleDateString("fr-FR", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </h4>
          <p className="bookings-daily__subtitle">
            {activeItemCount} {t(`${tKey}confirmedBookings`)}
          </p>
        </div>
        {onToggleFullscreen && (
          <Button variant="white" icon onClick={onToggleFullscreen}>
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </Button>
        )}
      </div>

      <div className="bookings-daily__grid">
        <div className="bookings-daily__grid-inner">
          {/* Column Headers */}
          <div className="bookings-daily__table-headers">
            <div className="bookings-daily__time-header">
              {t(`${tKey}time`)}
            </div>
            {columns.map((col) => (
              <div
                key={col.id}
                className={`bookings-daily__table-header ${col.isNoTable ? "bookings-daily__table-header--no-table" : ""}`}
              >
                <div className="bookings-daily__table-name">{col.name}</div>
                {col.info && (
                  <div className="bookings-daily__table-info">{col.info}</div>
                )}
              </div>
            ))}
          </div>

          {/* Time Rows */}
          {timeSlots.map((timeSlot) => (
            <div key={timeSlot} className="bookings-daily__row">
              <div className="bookings-daily__time-cell">{timeSlot}</div>
              {columns.map((col) => {
                const slotItems = getItemsForTableAndTime(col.id, timeSlot);
                return (
                  <div key={col.id} className="bookings-daily__table-cell">
                    {slotItems.map((item) => {
                      if (!isItemStart(item, timeSlot)) return null;

                      const isOrder = item.type === "order";
                      const canceled = isCanceledItem(item);

                      let cardClass = "booking-card";
                      if (canceled) {
                        cardClass += " booking-card--canceled";
                      } else if (item.colorTheme) {
                        cardClass += ` booking-card--${item.colorTheme}`;
                      }

                      return (
                        <div
                          key={item.itemId}
                          className={cardClass}
                          onClick={() => onViewItem(item)}
                        >
                          <div className="booking-card__name">
                            {item.guestName}
                          </div>
                          <div className="booking-card__details-row">
                            <span className="booking-card__detail">
                              <FontAwesomeIcon
                                icon={faClock}
                                className="fa-icon"
                              />
                              {new Date(item.bookingDate).toLocaleTimeString(
                                "fr-FR",
                                { hour: "2-digit", minute: "2-digit" },
                              )}
                            </span>
                            <span className="booking-card__detail">
                              <FontAwesomeIcon
                                icon={faUsers}
                                className="fa-icon"
                              />
                              {item.partySize}{" "}
                              {t("dashboard.bookings.detail.guests")}
                            </span>
                            {isOrder && (
                              <FontAwesomeIcon
                                icon={faBox}
                                className="booking-card__order-icon"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DailyView;
